import FormResource from "@/module/Form/Resources/FormResource";
import FormItem from "@/module/FormItem";
import FormItemGroup from "@/module/FormItemGroup";
import FormOrderBump from "@/module/FormOrderBump";
import FormShippingItem from "@/module/FormShippingItem";
import FormPaymentMethodItem from "@/module/FormPaymentMethodItem";
import FormItemPrice from "@/module/FormItemPrice";

export default class FapiFormDataProvider {
  private formResource: FormResource;
  private _formItems: FormItem[] = [];
  private _itemGroups: FormItemGroup[] = [];
  private _orderBumps: FormOrderBump[] = [];
  private _shippingMethods: FormShippingItem[] = [];
  private _upsellItems: FormItem[] = [];
  private _collectOnDeliveryPaymentMethodFormItem: FormPaymentMethodItem | null =
    null;

  constructor(fapiFormBaseData: FormResource) {
    this.formResource = fapiFormBaseData;
  }

  get formItems(): FormItem[] {
    return this._formItems;
  }

  get itemGroups(): FormItemGroup[] {
    return this._itemGroups;
  }

  get orderBumps(): FormOrderBump[] {
    return this._orderBumps;
  }

  get shippingMethods(): FormShippingItem[] {
    return this._shippingMethods;
  }

  get upsellItems(): FormItem[] {
    return this._upsellItems;
  }

  get collectOnDeliveryPaymentMethodFormItem(): FormPaymentMethodItem | null {
    return this._collectOnDeliveryPaymentMethodFormItem;
  }

  buildItemTemplatePrices(itemPrices: any[]): FormItemPrice[] {
    const newPrices: FormItemPrice[] = [];
    itemPrices.forEach((itemPrice) => {
      newPrices.push(new FormItemPrice(itemPrice));
    });

    return newPrices;
  }

  build(): void {
    this._itemGroups.push(
      new FormItemGroup(
        0,
        "",
        [],
        true,
        this.formResource.allow_multiple_items,
      ),
    );

    for (const itemGroup of this.formResource.item_groups) {
      this._itemGroups.push(
        new FormItemGroup(
          itemGroup.id,
          itemGroup.title,
          [],
          itemGroup.required,
          itemGroup.allow_multiple_items,
        ),
      );
    }

    for (const index in this.formResource.items) {
      const item = this.formResource.items[index];
      const itemTemplate =
        this.formResource.item_templates[item["item_template"]];

      const formItem = new FormItem(
        item.id,
        itemTemplate.id,
        this.buildItemTemplatePrices(itemTemplate.prices),
        Boolean(itemTemplate.including_vat),
        itemTemplate.vat,
        itemTemplate.name,
        itemTemplate.description,
        itemTemplate.public_description ?? null,
        item.required,
        itemTemplate.disable_discount,
        itemTemplate.type,
        itemTemplate.reverse_charge,
        itemTemplate.show_stock_information,
        itemTemplate.enable_stock,
        itemTemplate.stock,
        itemTemplate.allow_change_count,
        itemTemplate.sold_out,
        item.group,
        itemTemplate.periodic ? itemTemplate.period : null,
        itemTemplate.metadata,
        itemTemplate.code,
        itemTemplate.image_url,
      );

      const itemGroup = this.itemGroups.find(
        (ele) => ele.id === formItem.groupId,
      );

      if (!itemGroup) {
        continue;
      }

      this._formItems.push(formItem);
      itemGroup.items.push(formItem);
    }

    for (const index in this.formResource.order_bumps) {
      const orderBumpData = this.formResource.order_bumps[index];
      const itemTemplate = this.formResource.order_bump_item_templates.find(
        (item) => {
          return orderBumpData.item_template === item.id;
        },
      );

      if (itemTemplate === undefined) {
        continue;
      }

      const formItem = new FormItem(
        orderBumpData.id,
        itemTemplate.id,
        this.buildItemTemplatePrices(itemTemplate.prices),
        Boolean(itemTemplate.including_vat),
        itemTemplate.vat,
        itemTemplate.name,
        itemTemplate.description,
        itemTemplate.public_description ?? null,
        false,
        itemTemplate.disable_discount,
        itemTemplate.type,
        itemTemplate.reverse_charge,
        itemTemplate.show_stock_information,
        itemTemplate.enable_stock,
        itemTemplate.stock,
        itemTemplate.allow_change_count,
        itemTemplate.sold_out,
        null,
        itemTemplate.periodic ? itemTemplate.period : null,
        itemTemplate.metadata,
        itemTemplate.code,
        itemTemplate.image_url,
      );

      this._orderBumps.push(
        new FormOrderBump(
          orderBumpData.id,
          formItem,
          orderBumpData.name,
          orderBumpData.description,
        ),
      );
    }

    if (this.formResource.allow_upsells) {
      for (const index in this.formResource.upsells) {
        const upsell = this.formResource.upsells[index];
        const itemTemplate =
          this.formResource.item_templates[upsell["item_template"]];

        const upsellItem = new FormItem(
          upsell.id,
          itemTemplate.id,
          this.buildItemTemplatePrices(itemTemplate.prices),
          Boolean(itemTemplate.including_vat),
          itemTemplate.vat,
          itemTemplate.name,
          itemTemplate.description,
          itemTemplate.public_description ?? null,
          false,
          itemTemplate.disable_discount,
          itemTemplate.type,
          itemTemplate.reverse_charge,
          itemTemplate.show_stock_information,
          itemTemplate.enable_stock,
          itemTemplate.stock,
          itemTemplate.allow_change_count,
          itemTemplate.sold_out,
          null,
          itemTemplate.periodic ? itemTemplate.period : null,
          itemTemplate.metadata,
          itemTemplate.code,
          itemTemplate.image_url,
        );

        this._upsellItems.push(upsellItem);
      }
    }

    if (this.formResource.allow_shipping_methods) {
      for (const shippingMethodResource of this.formResource.shipping_methods) {
        const formShippingItem = new FormShippingItem(shippingMethodResource);

        this._shippingMethods.push(formShippingItem);
      }
    }

    if (this.formResource.collect_on_delivery_payment_method !== null) {
      this._collectOnDeliveryPaymentMethodFormItem = new FormPaymentMethodItem(
        this.formResource.collect_on_delivery_payment_method,
      );
    }
  }
}
